/**
 * AgentCheckIn — lightweight persistence helper that records whether the
 * agent has clocked into a given run within a sliding time window. Used by
 * the agent campaign page to gate activities (Sales, Survey, etc.) until the
 * agent has actively checked in.
 *
 * Records are scoped by the active user id so that signing out and signing
 * in as a different user does NOT inherit the previous user's check-in
 * state. The active user id is set once after auth via `setActiveUser()`
 * and consulted by every read/write.
 *
 * Kept intentionally tiny and dependency-free so it can be set from anywhere
 * the CLOCK_IN_OUT mutation succeeds (e.g. RunAgent.tsx).
 */

const PREFIX = 'hokela.agent.checkedIn.';
const ACTIVE_USER_KEY = 'hokela.agent.activeUserId';
const DEFAULT_TTL_MS = 3 * 60 * 60 * 1000; // 3h shift window

/** Window CustomEvent name dispatched whenever a check-in record changes. */
export const CHECK_IN_EVENT = 'hokela.checkin.changed';

interface CheckInRecord {
  runId: string;
  userId: string;
  at: number;
  exp: number;
}

const key = (userId: string, runId: string) => `${PREFIX}${userId}:${runId}`;

function emit(runId: string) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(CHECK_IN_EVENT, { detail: { runId } }));
  } catch {
    // ignore
  }
}

function getActiveUserId(explicit?: string | null): string | null {
  if (explicit) return explicit;
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACTIVE_USER_KEY);
  } catch {
    return null;
  }
}

/** Purge every persisted check-in record (any user, any run). */
function purgeAll() {
  if (typeof window === 'undefined') return;
  try {
    const ls = window.localStorage;
    const toRemove: string[] = [];
    for (let i = 0; i < ls.length; i += 1) {
      const k = ls.key(i);
      if (k && k.startsWith(PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => ls.removeItem(k));
  } catch {
    // ignore
  }
}

export const AgentCheckIn = {
  /**
   * Bind subsequent reads/writes to a specific user. Call once after
   * authentication (e.g. when session resolves). If the active user
   * changes, all previously persisted check-in records are purged so the
   * new user starts clean.
   */
  setActiveUser(userId?: string | null) {
    if (typeof window === 'undefined') return;
    try {
      const current = window.localStorage.getItem(ACTIVE_USER_KEY);
      if (!userId) {
        // sign out — wipe everything
        window.localStorage.removeItem(ACTIVE_USER_KEY);
        purgeAll();
        emit('*');
        return;
      }
      if (current && current !== userId) {
        // different user signed in — drop stale records
        purgeAll();
      }
      window.localStorage.setItem(ACTIVE_USER_KEY, userId);
    } catch {
      // ignore
    }
  },

  /** Clear every persisted check-in record. Call on logout. */
  reset() {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(ACTIVE_USER_KEY);
      purgeAll();
      emit('*');
    } catch {
      // ignore
    }
  },

  set(runId: string, userId?: string | null, ttlMs: number = DEFAULT_TTL_MS) {
    if (typeof window === 'undefined' || !runId) return;
    const uid = getActiveUserId(userId);
    if (!uid) return;
    try {
      const record: CheckInRecord = {
        runId,
        userId: uid,
        at: Date.now(),
        exp: Date.now() + ttlMs,
      };
      window.localStorage.setItem(key(uid, runId), JSON.stringify(record));
      emit(runId);
    } catch {
      // ignore quota errors
    }
  },

  isCheckedIn(runId?: string | null, userId?: string | null): boolean {
    if (typeof window === 'undefined' || !runId) return false;
    const uid = getActiveUserId(userId);
    if (!uid) return false;
    try {
      const raw = window.localStorage.getItem(key(uid, runId));
      if (!raw) return false;
      const record = JSON.parse(raw) as CheckInRecord;
      if (!record?.exp || record.userId !== uid) return false;
      return Date.now() < record.exp;
    } catch {
      return false;
    }
  },

  clear(runId: string, userId?: string | null) {
    if (typeof window === 'undefined' || !runId) return;
    const uid = getActiveUserId(userId);
    if (!uid) return;
    try {
      window.localStorage.removeItem(key(uid, runId));
      emit(runId);
    } catch {
      // ignore
    }
  },
};
